import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { finalize } from 'rxjs/operators';
import {
  FeatureAudienceRole,
  FeatureFlag,
  FeatureFlagAudienceOverride,
  FeatureFlagAudienceUpdate
} from '../../core/models/feature-flag.model';
import { FeatureFlagService } from '../../core/services/feature-flag.service';
import { ConfirmDialogComponent } from '../../shared/components/confirm-dialog/confirm-dialog.component';

interface OverrideDraft {
  originalRole?: string;
  originalOperatorType?: string | null;
  role: FeatureAudienceRole;
  operatorType: string;
  isEnabled: boolean;
  isNew: boolean;
}

@Component({
  selector: 'app-feature-settings',
  standalone: true,
  imports: [CommonModule, FormsModule, ConfirmDialogComponent],
  templateUrl: './feature-settings.component.html',
  styleUrls: ['./feature-settings.component.css']
})
export class FeatureSettingsComponent implements OnInit {
  readonly roles = ['admin', 'platform_employee', 'operator', 'passenger', 'driver'];
  readonly knownOperatorTypes = ['bus', 'truck', 'taxi'];
  flags: FeatureFlag[] = [];
  drafts: Record<string, OverrideDraft[]> = {};
  saving = new Set<string>();
  inlineErrors: Record<string, string> = {};
  isLoading = true;
  errorMessage = '';
  successMessage = '';
  pendingDisable: FeatureFlag | null = null;

  constructor(private featureFlags: FeatureFlagService) {}

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.isLoading = true;
    this.errorMessage = '';
    this.featureFlags.loadAdminFeatures().pipe(finalize(() => this.isLoading = false)).subscribe({
      next: flags => {
        this.flags = flags.map(flag => ({ ...flag, audienceOverrides: flag.audienceOverrides || [] }));
        this.flags.forEach(flag => this.resetDrafts(flag));
      },
      error: () => this.errorMessage = 'Feature management could not be loaded. Please try again.'
    });
  }

  requestDefaultChange(flag: FeatureFlag, enabled: boolean): void {
    if (this.saving.has(flag.key) || enabled === flag.isEnabled) return;
    if (!enabled && flag.isEnabled) {
      this.pendingDisable = flag;
      return;
    }
    this.saveDefault(flag, enabled);
  }

  confirmDisable(): void {
    const flag = this.pendingDisable;
    this.pendingDisable = null;
    if (flag) this.saveDefault(flag, false);
  }

  addOverride(flag: FeatureFlag): void {
    this.drafts[flag.key] = [
      ...(this.drafts[flag.key] || []),
      { role: 'operator', operatorType: '', isEnabled: true, isNew: true }
    ];
    this.inlineErrors[flag.key] = '';
  }

  roleChanged(flag: FeatureFlag, draft: OverrideDraft): void {
    if (draft.role !== 'operator') draft.operatorType = '';
    this.validateDrafts(flag);
  }

  operatorTypes(flag: FeatureFlag, draft: OverrideDraft): string[] {
    const custom = [
      draft.operatorType,
      ...(flag.audienceOverrides || []).map(item => item.operatorType || '')
    ].filter(Boolean);
    return [...new Set([...this.knownOperatorTypes, ...custom])];
  }

  saveOverride(flag: FeatureFlag, draft: OverrideDraft): void {
    if (!this.validateDrafts(flag)) return;
    const updates: FeatureFlagAudienceUpdate[] = [];
    const normalizedType = draft.role === 'operator' ? this.normalizeOperatorType(draft.operatorType) : null;
    if (!draft.isNew && (
      draft.originalRole !== draft.role
      || (draft.originalOperatorType || null) !== normalizedType
    )) {
      updates.push({
        role: draft.originalRole || draft.role,
        operatorType: draft.originalOperatorType || null,
        isEnabled: null
      });
    }
    updates.push({ role: draft.role, operatorType: normalizedType, isEnabled: draft.isEnabled });
    this.patch(flag, updates, 'Audience rule saved.');
  }

  removeOverride(flag: FeatureFlag, draft: OverrideDraft): void {
    if (draft.isNew) {
      this.drafts[flag.key] = this.drafts[flag.key].filter(item => item !== draft);
      this.inlineErrors[flag.key] = '';
      return;
    }
    this.patch(flag, [{
      role: draft.originalRole || draft.role,
      operatorType: draft.originalOperatorType || null,
      isEnabled: null
    }], 'Audience rule removed.');
  }

  private saveDefault(flag: FeatureFlag, enabled: boolean): void {
    this.saving.add(flag.key);
    this.inlineErrors[flag.key] = '';
    this.featureFlags.updateFeature(flag.key, { isEnabled: enabled }).pipe(
      finalize(() => this.saving.delete(flag.key))
    ).subscribe({
      next: confirmed => this.acceptServerFlag(flag, confirmed, `${flag.displayName} default has been ${enabled ? 'enabled' : 'disabled'}.`),
      error: error => this.inlineErrors[flag.key] = this.backendMessage(error, 'The default state could not be updated.')
    });
  }

  private patch(flag: FeatureFlag, audienceOverrides: FeatureFlagAudienceUpdate[], message: string): void {
    this.saving.add(flag.key);
    this.inlineErrors[flag.key] = '';
    this.featureFlags.updateFeature(flag.key, { audienceOverrides }).pipe(
      finalize(() => this.saving.delete(flag.key))
    ).subscribe({
      next: confirmed => this.acceptServerFlag(flag, confirmed, message),
      error: error => this.inlineErrors[flag.key] = this.backendMessage(error, 'The audience rule could not be saved.')
    });
  }

  private acceptServerFlag(previous: FeatureFlag, confirmed: FeatureFlag, message: string): void {
    const normalized = { ...previous, ...confirmed, audienceOverrides: confirmed.audienceOverrides || [] };
    const index = this.flags.findIndex(item => item.key === previous.key);
    if (index >= 0) {
      this.flags[index] = normalized;
      this.flags = [...this.flags];
    }
    this.resetDrafts(normalized);
    this.successMessage = message;
    this.featureFlags.refreshEffectiveFeatures().subscribe({ error: () => undefined });
  }

  private resetDrafts(flag: FeatureFlag): void {
    this.drafts[flag.key] = (flag.audienceOverrides || []).map(override => this.toDraft(override));
    this.inlineErrors[flag.key] = '';
  }

  private toDraft(override: FeatureFlagAudienceOverride): OverrideDraft {
    return {
      originalRole: override.role,
      originalOperatorType: override.operatorType || null,
      role: override.role,
      operatorType: override.operatorType || '',
      isEnabled: override.isEnabled,
      isNew: false
    };
  }

  private validateDrafts(flag: FeatureFlag): boolean {
    const seen = new Set<string>();
    for (const draft of this.drafts[flag.key] || []) {
      const type = draft.role === 'operator' ? this.normalizeOperatorType(draft.operatorType) : '';
      const combination = `${draft.role}/${type}`;
      if (seen.has(combination)) {
        this.inlineErrors[flag.key] = 'Each role and operator-type combination can only appear once.';
        return false;
      }
      seen.add(combination);
    }
    this.inlineErrors[flag.key] = '';
    return true;
  }

  private normalizeOperatorType(value: string): string | null {
    const normalized = String(value || '').trim().toLowerCase().replace(/[\s-]+/g, '_');
    return normalized || null;
  }

  private backendMessage(error: any, fallback: string): string {
    const validation = error?.error?.errors;
    if (validation && typeof validation === 'object') {
      return Object.values(validation).flat().join(' ');
    }
    return error?.error?.detail || error?.error?.title || error?.error?.message || fallback;
  }

  trackByKey(_index: number, flag: FeatureFlag): string {
    return flag.key;
  }
}
